import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { CSSProperties, useRef } from 'react'

import { Option } from 'types/option'
import { colors } from 'variables'

interface Props<TValue> {
  options: Option<TValue>[]
  selected: TValue
  render?: (label: string) => JSX.Element
  onChange: (value: TValue) => void
}

/**
 * 汎用的なタブ切り替えコンポーネント
 * 切り替え部分の制御だけ行い、タブ内の描画は別途行う
 */
export default function TabComponent<TValue>(props: Props<TValue>) {
  const tabRefs = useRef<(HTMLElement | null)[]>([])
  const handleClick = (tab: Option<TValue>, index: number) => () => {
    const target = tabRefs.current[index]
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }

    props.onChange(tab.value)
  }

  return (
    <>
      <Box style={containerStyle}>
        {props.options.map((option, i) => (
          <Button
            key={`tab-${option.label}-${i}`}
            ref={(el) => {
              tabRefs.current[i] = el
            }}
            style={tabStyle(option.value === props.selected)}
            onClick={handleClick(option, i)}
          >
            {props.render ? props.render(option.label) : option.label}
          </Button>
        ))}
      </Box>
    </>
  )
}

const containerStyle: CSSProperties = {
  display: 'flex',
  padding: '0 8px',
  gap: '4px',
  borderBottom: `2px solid ${colors.PRIMARY_COLOR}`,
  overflowX: 'scroll',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
}

const baseTabStyle: CSSProperties = {
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  borderBottomLeftRadius: '0px',
  borderBottomRightRadius: '0px',
  fontSize: '20px',
  height: '49px',
  padding: '0 16px',
  whiteSpace: 'nowrap',
  minWidth: 'fit-content',
}

const inactiveStyle: CSSProperties = {
  backgroundColor: '#E6E6E6',
  color: '#333',
}

const activeStyle: CSSProperties = {
  backgroundColor: colors.PRIMARY_COLOR,
  color: 'white',
}

const tabStyle = (isActive: boolean) => {
  return {
    ...baseTabStyle,
    ...(isActive ? activeStyle : inactiveStyle),
  }
}
